/**
 * Loading State Components
 * Provides various loading indicators including skeletons
 */

import React from "react";
import {
  Box,
  CircularProgress,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Card,
  Typography,
} from "@mui/material";

export interface LoadingSpinnerProps {
  /** Size of the spinner */
  size?: number;
  /** Full page centered loading */
  fullPage?: boolean;
  /** Loading message to display */
  message?: string;
}

/**
 * Simple centered loading spinner
 */
export const LoadingSpinner: React.FC<LoadingSpinnerProps> = ({
  size = 60,
  fullPage = false,
  message,
}) => {
  return (
    <Box
      role="status"
      aria-busy="true"
      aria-live="polite"
      aria-label={message || "Loading"}
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        py: fullPage ? 0 : 8,
        minHeight: fullPage ? "100vh" : "auto",
      }}
    >
      <CircularProgress size={size} aria-hidden="true" />
      {message && (
        <Typography
          variant="body2"
          sx={{
            color: "text.secondary",
            mt: 2
          }}>
          {message}
        </Typography>
      )}
    </Box>
  );
};

export interface TableSkeletonProps {
  /** Number of rows to show */
  rows?: number;
  /** Number of columns to show */
  columns?: number;
  /** Whether to show header */
  showHeader?: boolean;
  /** Whether to wrap in a Card */
  showCard?: boolean;
}

/**
 * Skeleton loader for tables
 */
export const TableSkeleton: React.FC<TableSkeletonProps> = ({
  rows = 5,
  columns = 5,
  showHeader = true,
  showCard = true,
}) => {
  const tableContent = (
    <TableContainer
      role="status"
      aria-busy="true"
      aria-live="polite"
      aria-label="Loading table data"
    >
      <Table>
        {showHeader && (
          <TableHead>
            <TableRow>
              {Array.from({ length: columns }).map((_, i) => (
                <TableCell key={i}>
                  <Skeleton variant="text" width={100} />
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
        )}
        <TableBody>
          {Array.from({ length: rows }).map((_, rowIdx) => (
            <TableRow key={rowIdx}>
              {Array.from({ length: columns }).map((_, colIdx) => (
                <TableCell key={colIdx}>
                  <Skeleton
                    variant="text"
                    width={colIdx === 0 ? 150 : colIdx === columns - 1 ? 40 : 100}
                  />
                </TableCell>
              ))}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );

  return showCard ? <Card>{tableContent}</Card> : tableContent;
};

export interface CardSkeletonProps {
  /** Height of the card */
  height?: number | string;
  /** Whether to show an image placeholder */
  showImage?: boolean;
  /** Number of text lines */
  lines?: number;
}

/**
 * Skeleton loader for cards
 */
export const CardSkeleton: React.FC<CardSkeletonProps> = ({
  height = 200,
  showImage = false,
  lines = 3,
}) => {
  return (
    <Card sx={{ height, p: 2 }}>
      {showImage && (
        <Skeleton
          variant="rectangular"
          height={100}
          sx={{ borderRadius: 1, mb: 2 }}
        />
      )}
      <Skeleton variant="text" width="60%" height={28} sx={{ mb: 1 }} />
      {Array.from({ length: lines }).map((_, i) => (
        <Skeleton
          key={i}
          variant="text"
          width={i === lines - 1 ? "40%" : "100%"}
        />
      ))}
    </Card>
  );
};

export interface FormSkeletonProps {
  /** Number of fields to show */
  fields?: number;
  /** Whether to show section headers */
  showSections?: boolean;
}

/**
 * Skeleton loader for forms
 */
export const FormSkeleton: React.FC<FormSkeletonProps> = ({
  fields = 4,
  showSections = false,
}) => {
  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
      {showSections && <Skeleton variant="text" width={150} height={32} />}
      {Array.from({ length: fields }).map((_, i) => (
        <Box key={i}>
          <Skeleton variant="text" width={100} height={20} sx={{ mb: 0.5 }} />
          <Skeleton variant="rectangular" height={56} sx={{ borderRadius: 1 }} />
        </Box>
      ))}
      <Box sx={{ display: "flex", gap: 2, mt: 2 }}>
        <Skeleton variant="rectangular" width={100} height={40} sx={{ borderRadius: 1 }} />
        <Skeleton variant="rectangular" width={100} height={40} sx={{ borderRadius: 1 }} />
      </Box>
    </Box>
  );
};

export interface DetailSkeletonProps {
  /** Number of detail rows */
  rows?: number;
}

/**
 * Skeleton loader for detail pages
 */
export const DetailSkeleton: React.FC<DetailSkeletonProps> = ({ rows = 6 }) => {
  return (
    <Card sx={{ p: 3 }}>
      <Skeleton variant="text" width={200} height={36} sx={{ mb: 3 }} />
      {Array.from({ length: rows }).map((_, i) => (
        <Box
          key={i}
          sx={{
            display: "flex",
            justifyContent: "space-between",
            py: 1.5,
            borderBottom: i < rows - 1 ? "1px solid" : "none",
            borderColor: "divider",
          }}
        >
          <Skeleton variant="text" width={120} />
          <Skeleton variant="text" width={180} />
        </Box>
      ))}
    </Card>
  );
};

/**
 * Full page loading fallback for lazy-loaded routes
 */
export const PageLoadingFallback: React.FC = () => {
  return (
    <Box
      role="status"
      aria-busy="true"
      aria-live="polite"
      aria-label="Loading page"
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        minHeight: 400,
        py: 8,
      }}
    >
      <CircularProgress size={48} aria-hidden="true" />
      <Typography
        variant="body2"
        sx={{
          color: "text.secondary",
          mt: 2
        }}>
        Loading...
      </Typography>
    </Box>
  );
};

/**
 * Dashboard stats skeleton
 */
export const StatsSkeleton: React.FC<{ count?: number }> = ({ count = 4 }) => {
  return (
    <Box
      role="status"
      aria-busy="true"
      aria-live="polite"
      aria-label="Loading statistics"
      sx={{
        display: "grid",
        gridTemplateColumns: { xs: "1fr", sm: "repeat(2, 1fr)", md: `repeat(${Math.min(count, 4)}, 1fr)` },
        gap: 2,
      }}
    >
      {Array.from({ length: count }).map((_, i) => (
        <Card key={i} sx={{ p: 3 }}>
          <Box sx={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between" }}>
            <Box sx={{ flex: 1 }}>
              <Skeleton variant="text" width={80} height={16} sx={{ mb: 1 }} />
              <Skeleton variant="text" width={60} height={40} />
            </Box>
            <Skeleton variant="circular" width={48} height={48} />
          </Box>
        </Card>
      ))}
    </Box>
  );
};

/**
 * List page skeleton (header + filters + table)
 */
export interface ListPageSkeletonProps {
  /** Number of table rows */
  rows?: number;
  /** Number of table columns */
  columns?: number;
  /** Whether to show filter bar */
  showFilters?: boolean;
  /** Whether to show action button in header */
  showHeaderAction?: boolean;
}

export const ListPageSkeleton: React.FC<ListPageSkeletonProps> = ({
  rows = 10,
  columns = 5,
  showFilters = true,
  showHeaderAction = true,
}) => {
  return (
    <Box role="status" aria-busy="true" aria-live="polite" aria-label="Loading page content">
      {/* Page Header */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          mb: 3,
        }}
      >
        <Box>
          <Skeleton variant="text" width={200} height={36} />
          <Skeleton variant="text" width={300} height={20} sx={{ mt: 0.5 }} />
        </Box>
        {showHeaderAction && (
          <Skeleton variant="rectangular" width={120} height={40} sx={{ borderRadius: 1 }} />
        )}
      </Box>

      {/* Filters */}
      {showFilters && (
        <Box sx={{ display: "flex", gap: 2, mb: 3 }}>
          <Skeleton variant="rectangular" width={250} height={40} sx={{ borderRadius: 1 }} />
          <Skeleton variant="rectangular" width={150} height={40} sx={{ borderRadius: 1 }} />
          <Skeleton variant="rectangular" width={150} height={40} sx={{ borderRadius: 1 }} />
        </Box>
      )}

      {/* Table */}
      <TableSkeleton rows={rows} columns={columns} showCard />
    </Box>
  );
};

/**
 * Detail page skeleton (header + info card + related data)
 */
export interface DetailPageSkeletonProps {
  /** Number of detail rows in main card */
  detailRows?: number;
  /** Whether to show related data section */
  showRelated?: boolean;
  /** Number of tabs to show (if using tabs) */
  tabs?: number;
}

export const DetailPageSkeleton: React.FC<DetailPageSkeletonProps> = ({
  detailRows = 8,
  showRelated = true,
  tabs = 0,
}) => {
  return (
    <Box role="status" aria-busy="true" aria-live="polite" aria-label="Loading details">
      {/* Page Header */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          mb: 3,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
          <Skeleton variant="circular" width={56} height={56} />
          <Box>
            <Skeleton variant="text" width={200} height={32} />
            <Skeleton variant="text" width={150} height={20} />
          </Box>
        </Box>
        <Box sx={{ display: "flex", gap: 1 }}>
          <Skeleton variant="rectangular" width={100} height={36} sx={{ borderRadius: 1 }} />
          <Skeleton variant="rectangular" width={100} height={36} sx={{ borderRadius: 1 }} />
        </Box>
      </Box>

      {/* Tabs if present */}
      {tabs > 0 && (
        <Box sx={{ display: "flex", gap: 2, mb: 3, borderBottom: 1, borderColor: "divider", pb: 1 }}>
          {Array.from({ length: tabs }).map((_, i) => (
            <Skeleton key={i} variant="text" width={80} height={32} />
          ))}
        </Box>
      )}

      {/* Main Content */}
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: showRelated ? { xs: "1fr", lg: "2fr 1fr" } : "1fr",
          gap: 3,
        }}
      >
        <DetailSkeleton rows={detailRows} />
        {showRelated && (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
            <Card sx={{ p: 2 }}>
              <Skeleton variant="text" width={150} height={24} sx={{ mb: 2 }} />
              {Array.from({ length: 3 }).map((_, i) => (
                <Box key={i} sx={{ display: "flex", gap: 2, mb: 2 }}>
                  <Skeleton variant="circular" width={40} height={40} />
                  <Box sx={{ flex: 1 }}>
                    <Skeleton variant="text" width="80%" />
                    <Skeleton variant="text" width="50%" />
                  </Box>
                </Box>
              ))}
            </Card>
          </Box>
        )}
      </Box>
    </Box>
  );
};

/**
 * Dashboard skeleton with stats and charts
 */
export const DashboardSkeleton: React.FC = () => {
  return (
    <Box role="status" aria-busy="true" aria-live="polite" aria-label="Loading dashboard">
      {/* Stats Row */}
      <StatsSkeleton count={4} />

      {/* Charts Row */}
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr", md: "repeat(2, 1fr)" },
          gap: 3,
          mt: 3,
        }}
      >
        <Card sx={{ p: 3, height: 350 }}>
          <Skeleton variant="text" width={150} height={28} sx={{ mb: 2 }} />
          <Skeleton variant="rectangular" height={280} sx={{ borderRadius: 1 }} />
        </Card>
        <Card sx={{ p: 3, height: 350 }}>
          <Skeleton variant="text" width={150} height={28} sx={{ mb: 2 }} />
          <Skeleton variant="rectangular" height={280} sx={{ borderRadius: 1 }} />
        </Card>
      </Box>

      {/* Recent Activity */}
      <Card sx={{ p: 3, mt: 3 }}>
        <Skeleton variant="text" width={180} height={28} sx={{ mb: 2 }} />
        {Array.from({ length: 5 }).map((_, i) => (
          <Box
            key={i}
            sx={{
              display: "flex",
              gap: 2,
              py: 2,
              borderBottom: i < 4 ? "1px solid" : "none",
              borderColor: "divider",
            }}
          >
            <Skeleton variant="circular" width={40} height={40} />
            <Box sx={{ flex: 1 }}>
              <Skeleton variant="text" width="60%" />
              <Skeleton variant="text" width="40%" height={16} />
            </Box>
            <Skeleton variant="text" width={80} />
          </Box>
        ))}
      </Card>
    </Box>
  );
};

export default LoadingSpinner;
